import { clsx } from 'clsx';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useOptionalCurrentUser } from '@/app-kit/current-user-context';
import { openBugReport } from '@/observability/bug-report-bus';
import { clampDrag, isDrag, type Offset } from './modal-drag';

type Props = {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Tailwind max-width class, e.g. "max-w-lg" (default), "max-w-2xl", "max-w-4xl". */
  size?: string;
  children: ReactNode;
  /** Footer content — typically Cancel + Confirm buttons. */
  footer?: ReactNode;
  /**
   * Suppress the in-header bug-report trigger. Set on the bug-report modal
   * itself, which must not offer to report itself.
   */
  hideBugReport?: boolean;
  /** Optional content centred in the header, between the title and the buttons. */
  headerCenter?: ReactNode;
};

/**
 * Native <dialog>-backed modal. Closes on Esc, click-outside, or the X button.
 * Focus trap + scroll-lock behaviour comes from the browser implementation.
 *
 * ⚠️ Carries its OWN bug-report trigger, and that is not a convenience.
 * `showModal()` puts the dialog in the browser's TOP LAYER and makes every
 * element outside it INERT — so the app-shell's header button is not merely
 * covered, it is unclickable, exactly when a modal is showing an error. No
 * z-index can fix that; the top layer is not part of the page's stacking
 * context, and inert governs interaction rather than paint.
 *
 * An operator hit this on preview 2026-08-26: to report a broken Bill modal she
 * had to close it first, which lost the very state the report was about (and
 * bled the console buffer into the NEXT report). The only element that can be
 * clicked while a modal is open is one INSIDE that modal — hence this button.
 *
 * Opens centred every time, and can be dragged by its header while open so the
 * fields behind it can be seen (they stay inert — seen, not clicked). The
 * limits live in `modal-drag.ts`.
 */
export function Modal({ open, onClose, title, size = 'max-w-lg', children, footer, hideBugReport, headerCenter }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  // ⚠️ The OPTIONAL accessor. `Modal` is a ui-kit primitive that component
  // tests render standalone (file-list, edit-field-group, adjustment-modal, …),
  // so the throwing `useCurrentUserContext` turned 48 of them into errors when
  // this was first written the obvious way. No provider means no trigger —
  // which is also right in production: with no resolved user there are no
  // grants, and this must fail closed.
  const currentUser = useOptionalCurrentUser();
  const canBugReport = !hideBugReport
    && (currentUser?.reachesOperation('report_bug') ?? false);

  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  // Set when a drag ends, read by the click the browser fires on release. A
  // drag released over the backdrop clicks the <dialog> itself, which would
  // otherwise close it.
  const justDragged = useRef(false);
  const headerRef = useRef<HTMLElement>(null);
  // Detaches the drag in progress, if any. A modal that closes or unmounts
  // mid-drag must not keep following the pointer.
  const endDrag = useRef<(() => void) | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (!open) endDrag.current?.();
    if (open && !dialog.open) {
      setOffset({ x: 0, y: 0 });
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => () => endDrag.current?.(), []);

  // A dragged box also moves without a drag: the browser keeps it centred, so
  // content that grows after open, or a smaller window, shifts it under the
  // same offset. Pull the header back into the window when either happens.
  useEffect(() => {
    if (!open) return;
    const dialog = ref.current;
    const head = headerRef.current;
    if (!dialog || !head) return;
    const keepHeaderInView = () => {
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      const d = clampDrag(dialog.getBoundingClientRect(), head.getBoundingClientRect().height, viewport, { x: 0, y: 0 });
      if (d.x || d.y) setOffset((o) => ({ x: o.x + d.x, y: o.y + d.y }));
    };
    window.addEventListener('resize', keepHeaderInView);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(keepHeaderInView);
    observer?.observe(dialog);
    return () => {
      window.removeEventListener('resize', keepHeaderInView);
      observer?.disconnect();
    };
  }, [open]);

  function onHeaderPointerDown(e: React.PointerEvent<HTMLElement>) {
    if (e.button !== 0 || (e.target as Element).closest('button')) return;
    const dialog = ref.current;
    if (!dialog) return;
    e.preventDefault();
    endDrag.current?.();
    const pointerId = e.pointerId;
    const box = dialog.getBoundingClientRect();
    const headerHeight = e.currentTarget.getBoundingClientRect().height;
    const start = { x: e.clientX, y: e.clientY };
    const from = offset;
    let moved = false;
    const onMove = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      const move = { x: ev.clientX - start.x, y: ev.clientY - start.y };
      if (!moved && !isDrag(move)) return;
      moved = true;
      const d = clampDrag(box, headerHeight, { width: window.innerWidth, height: window.innerHeight }, move);
      setOffset({ x: from.x + d.x, y: from.y + d.y });
    };
    // A cancelled pointer (the browser took a touch or pen gesture) sends no
    // pointerup, so the drag ends on pointercancel as well.
    const stop = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      endDrag.current = null;
    };
    const onCancel = (ev: PointerEvent) => {
      if (ev.pointerId === pointerId) stop();
    };
    const onUp = (ev: PointerEvent) => {
      if (ev.pointerId !== pointerId) return;
      stop();
      if (!moved) return;
      justDragged.current = true;
      // The release's click, if any, is dispatched before this runs; a drag
      // released outside the window gets no click and must not eat the next one.
      setTimeout(() => { justDragged.current = false; }, 0);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    endDrag.current = stop;
  }

  // Lock background scroll while the modal is open. The native <dialog>
  // backdrop catches pointer events but doesn't suppress wheel/touch
  // scroll on the document, so pin `overflow: hidden` on <html> for the
  // duration. Restore the previous inline value on close so we don't
  // stomp other consumers if they ever set it.
  useEffect(() => {
    if (!open) return;
    const root = document.documentElement;
    const prev = root.style.overflow;
    root.style.overflow = 'hidden';
    return () => { root.style.overflow = prev; };
  }, [open]);

  // Click outside (on the ::backdrop) closes — except the click that ends a drag.
  function onClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (justDragged.current) { justDragged.current = false; return; }
    if (e.target === ref.current) onClose();
  }

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={onClick}
      className={clsx(
        'rounded-[var(--radius-card)] p-0 shadow-xl backdrop:bg-black/40',
        'max-h-[90vh] w-[calc(100%-2rem)] m-auto',
        size,
      )}
      style={offset.x || offset.y ? { transform: `translate(${offset.x}px, ${offset.y}px)` } : undefined}
    >
      {/* Three columns, so the centre slot sits at the true centre whatever
          the widths of the title and the buttons. The header is the drag handle. */}
      <header
        ref={headerRef}
        onPointerDown={onHeaderPointerDown}
        className="grid cursor-move touch-none select-none grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-t-[var(--radius-card)] bg-slate-600 px-6 py-3 text-white"
      >
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        <div className="flex items-center">{headerCenter}</div>
        <div className="flex items-center gap-3 justify-self-end">
          {canBugReport && (
            <button
              type="button"
              onClick={() => openBugReport()}
              aria-label="Report a bug"
              title="Report a bug — captures this dialog and the page behind it"
              className="text-white/80 hover:text-white"
            >
              🐞
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-white/80 hover:text-white"
          >
            ✕
          </button>
        </div>
      </header>
      <div className="overflow-y-auto px-6 py-5 text-sm text-gray-900">{children}</div>
      {footer && (
        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 bg-gray-50 px-6 py-3 rounded-b-[var(--radius-card)]">
          {footer}
        </footer>
      )}
    </dialog>
  );
}
